// Custom uniqBy function to remove duplicates based on a property
export function uniqBy<T>(array: T[], key: keyof T): T[] {
    const seen = new Set();
    return array.filter((item) => {
        const value = item[key];
        if (seen.has(value)) {
            return false;
        }
        seen.add(value);
        return true;
    });
}

// Custom flatten function to flatten array one level deep
export function flatten<T>(array: T[][]): T[] {
    const result: T[] = [];
    for (let i = 0; i < array.length; i++) {
        result.push(...array[i]);
    }
    return result;
}

/**
 * Converts an array into an object keyed by a specific property.
 * @param array - The array to transform
 * @param key - The property name to use as the key
 * @returns An object where keys are the property values and values are the array items
 * @example keyBy([{id: 1, name: 'a'}, {id: 2, name: 'b'}], 'id') // {"1": {id: 1, name: 'a'}, "2": {id: 2, name: 'b'}}
 */
export function keyBy<T>(array: T[], key: keyof T): Record<string, T> {
    const result: Record<string, T> = {};
    for (let i = 0; i < array.length; i++) {
        const item = array[i];
        const keyValue = String(item[key]);
        result[keyValue] = item;
    }
    return result;
}

/**
 * Transforms all values in an object using an iteratee function or by extracting a property.
 * @param obj - The object to transform
 * @param iteratee - Either a function to transform values or a property key to extract
 * @returns A new object with transformed values
 * @example mapValues({a: {val: 1}, b: {val: 2}}, (v) => v.val * 2) // {a: 2, b: 4}
 * @example mapValues({a: {val: 1}, b: {val: 2}}, 'val') // {a: 1, b: 2}
 */
export function mapValues<T, U>(
    obj: Record<string, T>,
    iteratee: ((value: T, key: string) => U) | keyof T,
): Record<string, U> {
    const result: Record<string, U> = {};

    // Check if iteratee is a function or a property key
    const isFunction = typeof iteratee === "function";

    for (const k in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, k)) {
            result[k] = isFunction
                ? (iteratee as (value: T, key: string) => U)(obj[k], k)
                : (obj[k][iteratee] as unknown as U);
        }
    }
    return result;
}

/**
 * Creates a new object with only the specified properties from the source object.
 * @param obj - The source object
 * @param keys - Array of property keys to pick
 * @returns A new object containing only the specified keys
 * @example pick({a: 1, b: 2, c: 3}, ['a', 'c']) // {a: 1, c: 3}
 */
export function pick<T extends object, K extends keyof T>(
    obj: T,
    keys: K[],
): Pick<T, K> {
    const result = {} as Pick<T, K>;
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (key in obj) {
            result[key] = obj[key];
        }
    }
    return result;
}

/**
 * Finds the index of the first element in an array that matches the predicate.
 * @param array - The array to search
 * @param predicate - Function that tests each element
 * @returns The index of the first matching element, or -1 if not found
 * @example findIndex([1, 2, 3, 4], (n) => n > 2) // 2
 */
export function findIndex<T>(
    array: T[],
    predicate: (value: T, index: number, array: T[]) => boolean,
): number {
    for (let i = 0; i < array.length; i++) {
        if (predicate(array[i], i, array)) {
            return i;
        }
    }
    return -1;
}

/**
 * Finds the index of the last element in an array that matches the predicate.
 * Searches from right to left.
 * @param array - The array to search
 * @param predicate - Function that tests each element
 * @returns The index of the last matching element, or -1 if not found
 * @example findLastIndex([1, 2, 3, 2, 1], (n) => n === 2) // 3
 */
export function findLastIndex<T>(
    array: T[],
    predicate: (value: T, index: number, array: T[]) => boolean,
): number {
    for (let i = array.length - 1; i >= 0; i--) {
        if (predicate(array[i], i, array)) {
            return i;
        }
    }
    return -1;
}

/**
 * Converts a string to start case (capitalizes the first letter of each word).
 * Handles camelCase, snake_case, kebab-case, and space-separated strings.
 * @param str - The string to convert
 * @returns The string in start case format
 * @example startCase('hello-world') // "Hello World"
 * @example startCase('helloWorld') // "Hello World"
 * @example startCase('hello_world') // "Hello World"
 */
export function startCase(str: string): string {
    return (
        str
            // Split on camelCase, snake_case, kebab-case, spaces
            .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase
            .replace(/[_-]/g, " ") // snake_case, kebab-case
            .trim()
            .split(/\s+/) // Split into words
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ")
    );
}

/**
 * Converts a string to lower case with spaces between words.
 * Handles camelCase, snake_case, and kebab-case.
 * @param str - The string to convert
 * @returns The string in lower case with spaces
 * @example lowerCase('HelloWorld') // "hello world"
 * @example lowerCase('hello-world') // "hello world"
 * @example lowerCase('hello_world') // "hello world"
 */
export function lowerCase(str: string): string {
    return (
        str
            // Split on camelCase
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            // Replace underscores and hyphens with spaces
            .replace(/[_-]/g, " ")
            // Replace multiple spaces with single space
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase()
    );
}

/**
 * Recursively flattens a nested array to a single level.
 * @param array - The array to flatten (can be deeply nested)
 * @returns A completely flattened array
 * @example flattenDeep([1, [2, [3, [4]]]]) // [1, 2, 3, 4]
 * @example flattenDeep([[1, 2], [3, [4, 5]]]) // [1, 2, 3, 4, 5]
 */
export function flattenDeep<T>(array: any[]): T[] {
    const result: T[] = [];

    for (let i = 0; i < array.length; i++) {
        if (Array.isArray(array[i])) {
            result.push(...flattenDeep<T>(array[i] as T[]));
        } else {
            result.push(array[i] as T);
        }
    }
    return result;
}

/**
 * Creates a deep clone of an object, array, or primitive value.
 * Handles nested objects, arrays, and Date instances.
 * @param obj - The value to clone
 * @returns A deep copy of the input value
 * @example cloneDeep({a: {b: 1}}) // {a: {b: 1}} (new reference)
 * @example cloneDeep([1, [2, 3]]) // [1, [2, 3]] (new reference)
 */
export function cloneDeep<T>(obj: T): T {
    if (obj === null || typeof obj !== "object") {
        return obj;
    }

    if (obj instanceof Date) {
        return new Date(obj.getTime()) as T;
    }

    if (Array.isArray(obj)) {
        const arrCopy: unknown[] = [];
        for (let i = 0; i < obj.length; i++) {
            arrCopy[i] = cloneDeep(obj[i]);
        }
        return arrCopy as T;
    }

    const objCopy = {} as T;
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            objCopy[key as keyof T] = cloneDeep(obj[key]);
        }
    }
    return objCopy;
}

/**
 * Safely retrieves a nested property value from an object using a path.
 * Returns undefined if the path doesn't exist, or defaultValue if provided.
 * @param obj - The object to query
 * @param path - The path to the property (string with dots or array)
 * @param defaultValue - Optional value to return instead of undefined
 * @returns The resolved value, defaultValue if provided and path doesn't exist, or undefined
 * @example get({a: {b: {c: 3}}}, 'a.b.c') // 3
 * @example get({a: {b: {c: 3}}}, 'a.b.x') // undefined
 * @example get({a: {b: {c: 3}}}, 'a.b.x', 'default') // 'default'
 * @example get({items: [1, 2, 3]}, 'items[1]') // 2
 */
export function get<T = any>(
    obj: any,
    path: string | string[],
    defaultValue?: T,
): T | undefined {
    // Convert string path to array if needed
    const pathArray = Array.isArray(path)
        ? path
        : path
              .replace(/\[(\d+)\]/g, ".$1") // Convert array[0] to array.0
              .split(".")
              .filter(Boolean); // Remove empty strings

    // Traverse the object
    let result: unknown = obj;
    for (const key of pathArray) {
        if (result == null) {
            return defaultValue;
        }
        result = (result as Record<string, unknown>)[key];
    }

    return result !== undefined ? (result as T) : defaultValue;
}
